
import { Options, Vue } from 'vue-class-component'
import { markRaw } from 'vue'
import { getCustomerInfo } from '@/services/line-web-loyalty-bff/v1/me'
import { Watch } from 'vue-property-decorator'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faAngleRight, faRotateLeft, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import liff from '@line/liff'
import { useStore } from '@/store'
import ModalIframeOptions from '@/interfaces/modal-iframe-options'
import { mapState } from 'vuex'
import { TieredPoint } from '@/interfaces/customer-info'
import { DATE_FORMAT, getDate } from '@/helpers/date'
import { getDisplayPoint } from '../../utils/functions'
import { addGAEvent } from '@/helpers/addGoogleAnalytic'
import QSkeleton from 'quasar/src/components/skeleton/QSkeleton.js';
import Webview from '../webview/webview.vue'
import { addNewModal } from '@/utils/modal'
import { MyCouponInfoPayload } from '@/interfaces/store/myCouponInfo'
import * as meService from '@/services/line-web-loyalty-bff/v1/me'
import { alertMessageOptionsSomethingWentWrong } from '@/model/alert-options.model'

library.add(faAngleRight, faRotateLeft, faXmark)

@Options({
  computed: {
    ...mapState({
      isLoadingPoints: (state: any) => state.app.isLoadingPoints,
      points: (state: any) => state.customer.points,
      tieredPoints: (state: any) => state.customer.tieredPoints,
    }),
  },
  components: {
    FontAwesomeIcon,
    QSkeleton,
  },
  props: {
    forceUpdate: Boolean,
    expiryDateOfCoin: String,
    openModalBarcode: Function,
  },
})

export default class ClubcardLoyalty extends Vue {
  private idToken!: string
  private forceUpdate!: boolean
  private expiryDateOfCoin = ''
  private openModalBarcode!: () => void
  private currentTime = ''
  private points?: number = undefined
  private isLoading = false
  private disabledButton = false
  private spinTime = 500
  private tieredPoints: TieredPoint[] | undefined
  private isTooltipTieredPoints = false
  private isUpgrading = false
  store = useStore()

  // Lite tier = customerType 'online'
  get isLite(): boolean {
    return this.store.state.customer?.customerType === 'online'
  }

  @Watch('isLoadingPoints')
  onUpdatedIsLoadingPoints(newValue: boolean) {
    this.isLoading = newValue
  }

  @Watch('points')
  onUpdatedPoints(newValue: number | undefined) {
    this.points = newValue
    this.currentTime = this.getCurrentTime()
  }

  @Watch('tieredPoints')
  onUpdatedTieredPoints(newValue: TieredPoint[] | undefined) {
    this.tieredPoints = newValue
  }

  created() {
    this.isLoading = this.store.state.app.isLoadingPoints
    this.points = this.store.state.customer.points
    this.expiryDateOfCoin = this.store.state.customer.expiryDateOfCoin || ''
    this.currentTime = this.getCurrentTime()
  }

  isFirstAccess() {
    const query = this.$route?.query
    return query && query['is-first-linking'] === 'true'
  }

  startShopping() {
    const query = this.$route?.query
    
    if(query['is_disabled_deeplink'] !== 'true') {
      window.location.href = process.env.VUE_APP_DOWNLOAD_LOTUSS!
    }
    addGAEvent({
      event: 'mylotuss_start_shopping',
    })
  }

  pointsFormatter(number: any): string {
    if (typeof number === 'number') {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
    if (number === null || number === undefined) {
      return 'N/A'
    }
    return '0'
  }

  getCurrentTime(): string {
    const now = new Date()
    const hours = now.getHours()
    let minutes: number | string = now.getMinutes()
    return `${hours}:${minutes.toString().padStart(2, '0')}`
  }

  updateStoredPoints(points: number, tieredPoints: TieredPoint[]) {
    const customer = this.store.state.customer
    customer.points = points
    customer.tieredPoints = tieredPoints
    this.store.commit('customer/updateCustomer', customer)
  }

  async pointsRefresh() {
    addGAEvent({
      event: 'mylotuss_refresh_coin',
    })
    if (this.disabledButton) {
      return
    }
    this.isLoading = true
    this.disabledButton = true
    let refreshTime = Date.now()
    let result

    if (this.store.state.lineInfo.idToken) {
      this.idToken = this.store.state.lineInfo.idToken
      result = await getCustomerInfo(this.idToken, ['points'])
    }

    setTimeout(() => {
      this.disabledButton = false
    }, 10000)

    /*
      To check response time is less than the accepted interval time loading
      You can check the spin time in loading class
    */
    const difTime = Date.now() - refreshTime
    if (difTime <= this.spinTime) {
      setTimeout(() => {
        this.isLoading = false
      }, difTime)
    } else {
      this.isLoading = false
    }

    if (result?.httpStatusCode === 200) {
      const { points, tiered_points: tieredPoints } = result.json.data
      this.updateStoredPoints(points, tieredPoints)
      this.currentTime = this.getCurrentTime()
    } else if (result?.httpStatusCode === 401) {
      liff.logout()
      liff.login()
    }
  }

  getLabelPoints(): string {
    return this.points !== undefined ? getDisplayPoint(this.points) : 'N/A'
  }

  getLabelTieredPoints(): string | undefined {
    if (this.tieredPoints) {
      const expiringPoints = parseFloat(this.tieredPoints[0].expiringPoints)
      const points = getDisplayPoint(expiringPoints)
      const date = getDate(this.tieredPoints[0].date, DATE_FORMAT)

      return `${points} คอยน์ หมดอายุ ${date}`
    }
  }

  getLabelGoldTierExpiryDate(date?: string): string {
    return date ? `ก่อน ${getDate(date, DATE_FORMAT)}` : ''
  }

  onShowModalTieredPoints() {
    this.store.dispatch('modalTieredPoints/onOpen', true)
  }

  updateModalIframe(isActive: boolean, modalOptions: ModalIframeOptions) {
    this.store.commit('modalIframe/updateModalIframeActive', isActive)
    this.store.commit('modalIframe/updateModalOptionsIframe', modalOptions)
  }

  updateGAEvent(txtTab: string) {
    addGAEvent({
      event: 'mylotuss_icon_tab',
      icon_tab: txtTab,
    })
  }

  openCoinHistory() {
    this.store.dispatch('coinHistory/onOpen', true)
    addGAEvent({
      event: 'mylotuss_coins_expoiry_details',
    })
  }

  onShowTooltipTieredPoints() {
    this.isTooltipTieredPoints = !this.isTooltipTieredPoints
  }

  onCloseTooltipTieredPoints() {
    this.isTooltipTieredPoints = false
  }

  upgradeToLoyalty() {
    liff.openWindow({
      url: process.env.VUE_APP_ACTIVATE_LOYALTY_URL!
    })
  }

  showModalGoldMember() {
    addNewModal({
      type: 'modal-bottom-dynamic',
      name: 'Gold Member',
      component: markRaw(Webview),
      props: {
        title: 'Gold Member',
        link: process.env.VUE_APP_GOLD_MEMBER_URL!,
      },
    })
  }

  async upgradeCustomerTier() {
    if (this.isUpgrading) {
      return
    }
    this.isUpgrading = true
    this.store.commit('modalEditProfile/updateIsLoadingSpinner', true)
    try {
      const token = this.store.state.lineInfo.idToken
      if (token) {
        const result = await meService.upgradeCustomerTier(token)
        const statusCode = result?.json?.code
        if (statusCode === 200) {
          // Recheck again
          await this.store.dispatch('tier/fetchTier', { token })
          await this.store.dispatch('myCouponInfo/reloadAllCoupons', { token } as MyCouponInfoPayload)
        } else {
          this.store.dispatch('alertMessage/onOpen', alertMessageOptionsSomethingWentWrong)
          setTimeout(() => this.store.dispatch('alertMessage/onClose'), 3000)
        }
      }
    } catch {
      this.store.dispatch('alertMessage/onOpen', alertMessageOptionsSomethingWentWrong)
      setTimeout(() => this.store.dispatch('alertMessage/onClose'), 3000)
    } finally {
      this.isUpgrading = false
      this.store.commit('modalEditProfile/updateIsLoadingSpinner', false)
    }
  }
}
